import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { type SelectedAccount } from '../workspace-header/workspace-header-social-accounts'
import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import {
  type PostContent as LinkedInPostContent,
  type Settings as LinkedInSettings,
} from '@backend/lib/platforms/linkedin'
import { multiImageEntryUrl } from '@backend/lib/platforms/linkedin/multiImage'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'
import {
  type PostContent as InstagramPostContent,
  type Settings as InstagramSettings,
} from '@backend/lib/platforms/instagram'
import {
  type PostContent as TikTokPostContent,
  type Settings as TikTokSettings,
} from '@backend/lib/platforms/tiktok'
import {
  type PostContent as YouTubePostContent,
  type Settings as YouTubeSettings,
} from '@backend/lib/platforms/youtube'
import {
  type PostContent as FacebookPostContent,
  type Settings as FacebookSettings,
} from '@backend/lib/platforms/facebook'
import {
  buildPostContentFromWorkspace,
  type WorkspaceLike,
} from '@backend/lib/platforms/workspaceToPostContent'
import { trpc } from '@/config/trpc'
import { toast } from 'sonner'
import { usePostHog } from 'posthog-js/react'
import { TRPCClientError } from '@trpc/client'
import { captureError } from '@/lib/errors'
import { PostPreviewScheduleButton } from '@/components/post-preview/post-preview-schedule-button'
import {
  analyzePostType,
  checkCompatibility,
  type CaptionLengths,
} from '@/lib/workspace-media-validation'
import {
  validateWorkspacePreview,
  type PlatformAccountsMap,
  type PlatformAuthMap,
} from '@/lib/workspace-preview-validation'
import { WorkspacePreviewValidation } from './workspace-preview-validation'
import { WorkspacePreviewSyncStatus } from './workspace-preview-sync-status'
import { WorkspacePreviewPostStatus } from './workspace-preview-post-status'
import { type UpdateScheduledContentFn } from '@/lib/workspace-post-status'
import {
  type PlatformStatusState,
  WorkspacePreviewStatus,
  idlePlatformStatus,
} from './workspace-preview-status'
import { WorkspacePreviewToggle } from './workspace-preview-toggle'
import { WorkspacePreviewContent } from './workspace-preview-content'
import { WorkspacePreviewAccountSelection } from './workspace-preview-account-selection'
import type {
  Account,
  PlatformType,
  PublishablePlatformType,
} from '@backend/lib/db/schema/platform'
import { isPublishablePlatform } from '@backend/lib/db/schema/platform'
import { PostButton } from '@/components/ui/buttons/post-button'
import { SettingsIcon, StarsIcon } from '@/components/ui/icons/icons-new'
import { Button } from '@/components/ui/shadcn/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'
import { convertImagesToPdf } from '@/lib/images-to-pdf'
import { type WorkspaceCaptions, type WorkspaceTitles } from '@backend/lib/db/schema/workspace'
import { type ThumbnailCanvas } from '@backend/lib/db/typeRoots/workspaceMedia'

import type { CreatorInfo } from '@backend/lib/platforms/tiktok'

/** Probe a video URL to get its duration in seconds */
const getVideoDuration = (url: string): Promise<number> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      resolve(video.duration)
      video.src = ''
    }
    video.onerror = () => reject(new Error('Could not load video metadata'))
    video.src = url
  })

/**
 * Per-platform UI settings shape. Each platform owns its own slice of UI state
 * (the parent component is free to shape its underlying state however it wants;
 * it just assembles this map at the call site).
 *
 * Adding a new platform = adding one key here.
 */
export type PlatformUiSettings = {
  instagram: {
    postAsStory: boolean
    shareToFeed: boolean
    disableComments: boolean
    /** Facebook Page ID for the post's location tag. Empty = no location. */
    locationId: string
    /** AI-disclosure self-declaration, sent as `is_ai_generated`. */
    isAiGenerated: boolean
    /** Bake a visible AI label into image media before Instagram fetches it. */
    bakeAiLabel: boolean
    /** Appearance of that label. One decision for the whole post, mirrored onto
     *  each platform because `zSettings` is per platform. */
    aiLabelStyle?: AiLabelStyle
    /** IPTC claim to restate after a publish-path re-encode. */
    aiProvenance?: 'generated' | 'composite'
  }
  linkedin: {
    visibility: 'PUBLIC' | 'CONNECTIONS'
    disableReshare: boolean
    /** Workspace S3 URL of an SRT caption file. Empty = no captions. */
    captionsUrl: string
    /** LinkedIn has no AI-disclosure API field, so a visible label baked into
     *  the image is the only on-image disclosure available. */
    bakeAiLabel: boolean
    aiLabelStyle?: AiLabelStyle
    /** IPTC claim to restate after a publish-path re-encode. */
    aiProvenance?: 'generated' | 'composite'
  }
  tiktok: {
    creatorInfo: CreatorInfo | null
    onCreatorInfoChange?: (info: CreatorInfo | null) => void
    privacyLevel: string
    allowComments: boolean
    allowDuets: boolean
    allowStitches: boolean
    commercialContent: boolean
    yourBrand: boolean
    brandedContent: boolean
    /** AI-Generated Content disclosure flag passed to TikTok as `is_aigc`. */
    isAigc: boolean
    /** DIRECT_POST publishes immediately; MEDIA_UPLOAD pushes to inbox draft. */
    postMode: 'DIRECT_POST' | 'MEDIA_UPLOAD'
    /** Auto-add background music for photo carousels. Ignored for video posts. */
    autoAddMusic: boolean
    /** Bake a visible AI label into photo media. Photo posts only. */
    bakeAiLabel: boolean
    aiLabelStyle?: AiLabelStyle
    /** IPTC claim to restate after a publish-path re-encode. */
    aiProvenance?: 'generated' | 'composite'
  }
  youtube: {
    privacy: 'public' | 'unlisted' | 'private'
    postAsShort: boolean
    category: string
    /** Synthetic-media disclosure flag passed to YouTube as `status.containsSyntheticMedia`. */
    containsSyntheticMedia: boolean
    defaultLanguage: string
    defaultAudioLanguage: string
    notifySubscribers: boolean
    license: 'youtube' | 'creativeCommon'
    embeddable: boolean
    publicStatsViewable: boolean
    /** Per-locale title+description map as JSON string. Parsed at use-site. */
    localizationsJson: string
    /** Workspace S3 URL of an SRT/SBV/VTT caption file. Empty = no captions. */
    captionsUrl: string
    /** BCP-47 caption language (defaults to defaultAudioLanguage on backend). */
    captionsLanguage: string
    /** Playlist ID to add the uploaded video to. Empty = skip. */
    playlistId: string
  }
  facebook: {
    postAsDraft: boolean
    postAsStory: boolean
    postAsReel: boolean
    /** Facebook Page ID for the post's location tag. Empty = no location. */
    placeId: string
    /** JSON string per Meta's feed_targeting schema. Parsed at use-site. */
    feedTargetingJson: string
    /** JSON string per Meta's targeting schema. Parsed at use-site. */
    targetingJson: string
    /** Title for video posts, distinct from caption. */
    videoTitle: string
    /** Watch-ranking signal for video posts (BEAUTY_FASHION, MUSIC, …). */
    contentCategory: string
    /** AI-disclosure sent as `provenance_info`. Facebook wants the shape of AI
     *  involvement rather than a boolean, so this is derived from media
     *  provenance upstream. Undefined = disclosure off. */
    aiDisclosure?: { createdWithAi: boolean; editedWithAi: boolean }
    /** Bake a visible AI label into image media before Facebook fetches it. */
    bakeAiLabel: boolean
    aiLabelStyle?: AiLabelStyle
    /** IPTC claim to restate after a publish-path re-encode. */
    aiProvenance?: 'generated' | 'composite'
  }
}

/**
 * Whether the selected platform will publish with a visible AI label baked into
 * the image, so the preview can draw it.
 *
 * An explicit switch rather than an index lookup: YouTube has no `bakeAiLabel`
 * at all, because it publishes video and there is no video compositor on the
 * publish path. That absence is a real capability difference, so it should fail
 * to compile if someone adds a platform without deciding the question.
 */
const willBakeAiLabel = (settings: PlatformUiSettings, platform: PlatformType): boolean => {
  switch (platform) {
    // Stories bake whenever disclosure is on, regardless of the visible-label
    // switch: a STORIES container cannot carry `is_ai_generated`, so on media
    // without an embedded provenance marker the pill is the only disclosure
    // left. Mirrored from the publish path rather than inferred — if the preview
    // ignored it, a user would approve a clean story and publish one with a pill
    // burned into the pixels, which is the exact failure this overlay exists to
    // stop.
    case 'instagram':
      return (
        settings.instagram.bakeAiLabel ||
        (settings.instagram.postAsStory && settings.instagram.isAiGenerated)
      )
    case 'facebook':
      return (
        settings.facebook.bakeAiLabel ||
        (settings.facebook.postAsStory && Boolean(settings.facebook.aiDisclosure))
      )
    case 'linkedin':
      return settings.linkedin.bakeAiLabel
    case 'tiktok':
      return settings.tiktok.bakeAiLabel
    default:
      return false
  }
}

interface WorkspacePreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Appearance of the baked AI label. One decision for the whole post, so it
   *  sits here rather than inside `platformSettings`. Only used to draw the
   *  preview; the publish path reads it from each platform's settings. */
  aiLabelStyle?: AiLabelStyle
  /** Whether to draw the label in the preview. View-only: it does not change
   *  what publishes, so a post can carry a label the preview omits when the user
   *  has deliberately turned this off. */
  showAiLabelInPreview?: boolean
  /** Per-platform connected accounts list. */
  accounts: PlatformAccountsMap
  /** Per-platform authentication status. */
  isAuthenticated: PlatformAuthMap
  workspaceMedia: WorkspaceMedia[]
  captions: WorkspaceCaptions
  activePlatforms: PlatformType[]
  onActivePlatformsChange: (platforms: PlatformType[]) => void
  onSettingsClick?: () => void
  onOpenSettingsForConfirm?: (callback: () => void, label: string) => void
  postAsCarousel?: boolean
  publishAsDraft?: boolean
  workspaceId?: number
  /** Per-platform UI settings (privacy, draft flags, etc.). */
  platformSettings: PlatformUiSettings
  workspaceTitle: string
  platformTitles?: WorkspaceTitles
  thumbnail?: ThumbnailCanvas | null
  /** Receives the drift re-sync handler so the workspace header (outside the
   *  modal) can trigger it — the modal owns the projection machinery. */
  updateScheduledContentRef?: React.MutableRefObject<UpdateScheduledContentFn | null>
}

export function WorkspacePreviewModal({
  open,
  onOpenChange,
  aiLabelStyle,
  showAiLabelInPreview = true,
  accounts,
  isAuthenticated,
  workspaceMedia,
  captions,
  activePlatforms,
  onActivePlatformsChange,
  onSettingsClick,
  onOpenSettingsForConfirm,
  postAsCarousel = false,
  publishAsDraft = false,
  workspaceId,
  platformSettings,
  workspaceTitle,
  platformTitles,
  thumbnail,
  updateScheduledContentRef,
}: WorkspacePreviewModalProps) {
  // Per-platform UI settings (assembled by the parent into one map).
  const {
    postAsStory: instagramPostAsStory,
    shareToFeed: instagramShareToFeed,
    disableComments: instagramDisableComments,
    locationId: instagramLocationId,
  } = platformSettings.instagram
  const {
    visibility: linkedinVisibility,
    disableReshare: linkedinDisableReshare,
    captionsUrl: linkedinCaptionsUrl,
  } = platformSettings.linkedin
  const {
    creatorInfo: tiktokCreatorInfo,
    onCreatorInfoChange: onTiktokCreatorInfoChange,
    privacyLevel: tiktokPrivacyLevel,
    allowComments: tiktokAllowComments,
    allowDuets: tiktokAllowDuets,
    allowStitches: tiktokAllowStitches,
    commercialContent: tiktokCommercialContent,
    yourBrand: tiktokYourBrand,
    brandedContent: tiktokBrandedContent,
    isAigc: tiktokIsAigc,
    postMode: tiktokPostMode,
    autoAddMusic: tiktokAutoAddMusic,
  } = platformSettings.tiktok
  const {
    privacy: youtubePrivacy,
    postAsShort: youtubePostAsShort,
    category: youtubeCategory,
    containsSyntheticMedia: youtubeContainsSyntheticMedia,
    defaultLanguage: youtubeDefaultLanguage,
    defaultAudioLanguage: youtubeDefaultAudioLanguage,
    notifySubscribers: youtubeNotifySubscribers,
    license: youtubeLicense,
    embeddable: youtubeEmbeddable,
    publicStatsViewable: youtubePublicStatsViewable,
    localizationsJson: youtubeLocalizationsJson,
    captionsUrl: youtubeCaptionsUrl,
    captionsLanguage: youtubeCaptionsLanguage,
    playlistId: youtubePlaylistId,
  } = platformSettings.youtube
  const { theme } = useTheme()
  const navigate = useNavigate()

  const [selectedAccounts, setSelectedAccounts] = useState<SelectedAccount[]>([])

  const [isPosting, setIsPosting] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isPostingComplete, setIsPostingComplete] = useState(false)
  const [isSchedulingComplete, setIsSchedulingComplete] = useState(false)
  // Publish-only: generation targets like Meta Ads never reach this modal's
  // account-selection / post-content flows.
  const [selectedPlatform, setSelectedPlatform] = useState<PublishablePlatformType>(
    activePlatforms.includes('linkedin') ? 'linkedin' : 'instagram',
  )

  const [platformStatus, setPlatformStatus] = useState<PlatformStatusState>(idlePlatformStatus())
  const [postLinks, setPostLinks] = useState<
    Record<string, { link?: string; scheduledPostId?: number; isScheduled: boolean }>
  >({})
  const [tiktokProcessing, setTiktokProcessing] = useState(false)

  const posthog = usePostHog()
  const { mutateAsync: createPost } = trpc.platform.post.useMutation()
  const { mutateAsync: schedulePost } = trpc.scheduledPost.create.useMutation()
  const { mutateAsync: deleteScheduledPost } = trpc.scheduledPost.delete.useMutation()
  const { mutateAsync: updateScheduledPost } = trpc.scheduledPost.update.useMutation()
  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: copyAsset } = trpc.asset.copyAsset.useMutation()

  // Fetch TikTok creator info when a TikTok account is selected
  const selectedTiktokAccount = selectedAccounts.find((acc) => acc.platform === 'tiktok')
  const { data: fetchedCreatorInfo } = trpc.platform.creatorInfo.useQuery(
    { platform: 'tiktok', accountId: selectedTiktokAccount?.accountId ?? '' },
    { enabled: !!selectedTiktokAccount && open },
  )

  // Sync fetched creator info to parent
  useEffect(() => {
    if (fetchedCreatorInfo && onTiktokCreatorInfoChange) {
      onTiktokCreatorInfoChange(fetchedCreatorInfo)
    }
  }, [fetchedCreatorInfo, onTiktokCreatorInfoChange])

  // 5e: Poll TikTok post status after publishing
  const trpcUtils = trpc.useUtils()
  const pollTikTokPostStatus = (publishId: string) => {
    let attempts = 0
    const maxAttempts = 10
    const intervalMs = 5000
    setTiktokProcessing(true)

    const poll = async () => {
      attempts++
      try {
        const status = await trpcUtils.platform.postStatus.fetch({
          platform: 'tiktok',
          publishId,
        })

        if (status.status === 'PUBLISH_COMPLETE') {
          setTiktokProcessing(false)
          toast.success(
            tiktokContent.type === 'photo'
              ? 'Your TikTok photo post is now live!'
              : 'Your TikTok video is now live!',
          )
          return
        }

        if (status.status === 'FAILED') {
          setTiktokProcessing(false)
          setPlatformStatus((prev) => ({ ...prev, tiktok: 'error' }))
          toast.error(`TikTok publishing failed: ${status.fail_reason || 'Unknown error'}`)
          return
        }

        // Still processing — keep polling
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs)
        } else {
          setTiktokProcessing(false)
        }
      } catch {
        // Silently stop polling on error — the initial toast already informed the user
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs)
        } else {
          setTiktokProcessing(false)
        }
      }
    }

    setTimeout(poll, intervalMs)
  }

  // accounts by platform
  const accountsByPlatform: Record<string, SelectedAccount[]> = {}
  selectedAccounts.forEach((account) => {
    if (!accountsByPlatform[account.platform]) {
      accountsByPlatform[account.platform] = []
    }
    accountsByPlatform[account.platform]!.push(account)
  })

  // Workspace-level thumbnail (shared across all platforms)
  const videoThumbnailUrl = thumbnail?.file.url
  const videoThumbnailTimestampMs = thumbnail?.sourceTimestampMs

  // post content — routed through the shared builder so this modal, the
  // workspace chat agent, and the v1 REST/MCP layer all produce identical
  // postContent shapes from the same workspace inputs.
  const baseYoutubeTitle = platformTitles?.youtube || workspaceTitle
  const youtubeTitle = youtubePostAsShort ? `${baseYoutubeTitle} #Shorts` : baseYoutubeTitle
  const workspaceLike: WorkspaceLike = {
    title: workspaceTitle,
    captions,
    // Override titles.youtube with the modal's #Shorts-aware computed
    // title; the builder otherwise pulls titles.youtube verbatim.
    titles: { ...(platformTitles ?? {}), youtube: youtubeTitle },
    media: workspaceMedia,
  }
  const linkedInBuilt = buildPostContentFromWorkspace(workspaceLike, 'linkedin')
  const instagramBuilt = buildPostContentFromWorkspace(workspaceLike, 'instagram', {
    isStory: instagramPostAsStory,
  })
  const tiktokBuilt = buildPostContentFromWorkspace(workspaceLike, 'tiktok')
  const youtubeBuilt = buildPostContentFromWorkspace(workspaceLike, 'youtube')
  // Build a unified `{type: 'invalid'} | <postContent>` shape so the rest of
  // this function (which falls back to a degenerate postContent for preview
  // UI when validation fails) keeps working unchanged.
  const linkedInResult =
    linkedInBuilt.ok && linkedInBuilt.content.platform === 'linkedin'
      ? linkedInBuilt.content.postContent
      : {
          type: 'invalid' as const,
          reason: linkedInBuilt.ok ? 'platform mismatch' : linkedInBuilt.reason,
        }
  const instagramResult =
    instagramBuilt.ok && instagramBuilt.content.platform === 'instagram'
      ? instagramBuilt.content.postContent
      : {
          type: 'invalid' as const,
          reason: instagramBuilt.ok ? 'platform mismatch' : instagramBuilt.reason,
        }
  const tiktokResult =
    tiktokBuilt.ok && tiktokBuilt.content.platform === 'tiktok'
      ? tiktokBuilt.content.postContent
      : {
          type: 'invalid' as const,
          reason: tiktokBuilt.ok ? 'platform mismatch' : tiktokBuilt.reason,
        }
  const youtubeResult =
    youtubeBuilt.ok && youtubeBuilt.content.platform === 'youtube'
      ? youtubeBuilt.content.postContent
      : {
          type: 'invalid' as const,
          reason: youtubeBuilt.ok ? 'platform mismatch' : youtubeBuilt.reason,
        }
  const linkedInContentBase: LinkedInPostContent =
    linkedInResult.type !== 'invalid'
      ? linkedInResult
      : ({ type: 'text' as const, caption: captions.linkedin || '' } as LinkedInPostContent)
  // Inject video thumbnail + optional captions into LinkedIn video posts.
  // Captions are only meaningful when the user is publishing a video — for
  // other types we silently drop the setting rather than error.
  const linkedInContent: LinkedInPostContent = (() => {
    if (linkedInContentBase.type !== 'video') return linkedInContentBase
    let next = linkedInContentBase
    if (videoThumbnailUrl) next = { ...next, thumbUrl: videoThumbnailUrl }
    if (linkedinCaptionsUrl) next = { ...next, captionsUrl: linkedinCaptionsUrl }
    return next
  })()
  const instagramContentBase: InstagramPostContent =
    instagramResult.type !== 'invalid'
      ? instagramResult
      : ({
          type: 'image' as const,
          url: '',
          caption: captions.instagram || '',
        } as InstagramPostContent)
  // Inject video thumbnail into Instagram reels
  const instagramContentWithThumb: InstagramPostContent =
    instagramContentBase.type === 'reel' && videoThumbnailUrl
      ? { ...instagramContentBase, thumbUrl: videoThumbnailUrl }
      : instagramContentBase
  const instagramContent: InstagramPostContent = instagramLocationId
    ? { ...instagramContentWithThumb, locationId: instagramLocationId }
    : instagramContentWithThumb
  const instagramSettings: InstagramSettings = {
    ...(instagramDisableComments ? { commentEnabled: false } : {}),
    ...(instagramResult.type === 'reel' ? { shareToFeed: instagramShareToFeed } : {}),
    ...(platformSettings.instagram.isAiGenerated ? { isAiGenerated: true } : {}),
    // Sent independently of the label: an Instagram crop or a TikTok
    // transcode strips the IPTC marker whether or not anything visible is
    // drawn, so the claim has to travel even when no label is baked.
    ...(platformSettings.instagram.aiProvenance
      ? { aiProvenance: platformSettings.instagram.aiProvenance }
      : {}),
    ...(platformSettings.instagram.bakeAiLabel
      ? {
          bakeAiLabel: true,
          // Only sent alongside the label itself — the appearance of a
          // label that isn't being drawn is not a setting worth sending.
          ...(platformSettings.instagram.aiLabelStyle
            ? { aiLabelStyle: platformSettings.instagram.aiLabelStyle }
            : {}),
        }
      : {}),
  }
  const tiktokCommercialFields = tiktokCommercialContent
    ? {
        brandOrganicToggle: tiktokYourBrand,
        brandContentToggle: tiktokBrandedContent,
      }
    : {}
  const tiktokContentBase: TikTokPostContent =
    tiktokResult.type !== 'invalid'
      ? tiktokResult
      : ({
          type: 'video' as const,
          url: '',
          caption: captions.tiktok || '',
        } as TikTokPostContent)
  // Inject cover timestamp for TikTok (only supports frame-by-timestamp, not image upload).
  // Only applies to video posts — photo carousels have their own coverIndex field.
  const tiktokContent: TikTokPostContent =
    tiktokContentBase.type === 'video' && videoThumbnailTimestampMs != null
      ? { ...tiktokContentBase, coverTimestampMs: videoThumbnailTimestampMs }
      : tiktokContentBase
  const tiktokSettings: TikTokSettings = {
    privacyLevel: (tiktokPrivacyLevel as TikTokSettings['privacyLevel']) || 'SELF_ONLY',
    disableComment: !tiktokAllowComments,
    disableDuet: !tiktokAllowDuets,
    disableStitch: !tiktokAllowStitches,
    ...tiktokCommercialFields,
    isAigc: tiktokIsAigc,
    // Sent independently of the label: an Instagram crop or a TikTok
    // transcode strips the IPTC marker whether or not anything visible is
    // drawn, so the claim has to travel even when no label is baked.
    ...(platformSettings.tiktok.aiProvenance
      ? { aiProvenance: platformSettings.tiktok.aiProvenance }
      : {}),
    ...(platformSettings.tiktok.bakeAiLabel
      ? {
          bakeAiLabel: true,
          // Only sent alongside the label itself — the appearance of a
          // label that isn't being drawn is not a setting worth sending.
          ...(platformSettings.tiktok.aiLabelStyle
            ? { aiLabelStyle: platformSettings.tiktok.aiLabelStyle }
            : {}),
        }
      : {}),
    postMode: tiktokPostMode,
    ...(tiktokAutoAddMusic ? { autoAddMusic: true } : {}),
  }
  const youtubeContentBase: YouTubePostContent =
    youtubeResult.type !== 'invalid'
      ? youtubeResult
      : ({
          type: 'video' as const,
          url: '',
          title: youtubeTitle,
          description: captions.youtube || '',
        } as YouTubePostContent)
  // Inject video thumbnail into YouTube content
  const youtubeContent: YouTubePostContent = videoThumbnailUrl
    ? { ...youtubeContentBase, thumbUrl: videoThumbnailUrl }
    : youtubeContentBase
  const parseLocalizations = (
    raw: string,
  ): Record<string, { title: string; description: string }> | undefined => {
    const trimmed = raw.trim()
    if (!trimmed) return undefined
    try {
      const parsed = JSON.parse(trimmed)
      if (!parsed || typeof parsed !== 'object') return undefined
      // Shape-validate the entries; drop the field entirely if anything is off.
      for (const v of Object.values(parsed as Record<string, unknown>)) {
        if (
          !v ||
          typeof v !== 'object' ||
          typeof (v as { title?: unknown }).title !== 'string' ||
          typeof (v as { description?: unknown }).description !== 'string'
        ) {
          return undefined
        }
      }
      return parsed as Record<string, { title: string; description: string }>
    } catch {
      return undefined
    }
  }
  const youtubeLocalizations = parseLocalizations(youtubeLocalizationsJson)

  const youtubeSettings: YouTubeSettings = {
    privacyStatus: youtubePrivacy,
    ...(youtubeCategory ? { categoryId: youtubeCategory } : {}),
    containsSyntheticMedia: youtubeContainsSyntheticMedia,
    ...(youtubeDefaultLanguage && { defaultLanguage: youtubeDefaultLanguage }),
    ...(youtubeDefaultAudioLanguage && { defaultAudioLanguage: youtubeDefaultAudioLanguage }),
    notifySubscribers: youtubeNotifySubscribers,
    license: youtubeLicense,
    embeddable: youtubeEmbeddable,
    publicStatsViewable: youtubePublicStatsViewable,
    ...(youtubeLocalizations && { localizations: youtubeLocalizations }),
    ...(youtubeCaptionsUrl && { captionsUrl: youtubeCaptionsUrl }),
    ...(youtubeCaptionsLanguage && { captionsLanguage: youtubeCaptionsLanguage }),
    ...(youtubePlaylistId && { playlistId: youtubePlaylistId }),
  }
  const linkedinSettings: LinkedInSettings = {
    status: publishAsDraft ? 'DRAFT' : 'PUBLISHED',
    visibility: linkedinVisibility,
    isReshareDisabledByAuthor: linkedinDisableReshare,
    // Sent independently of the label: an Instagram crop or a TikTok
    // transcode strips the IPTC marker whether or not anything visible is
    // drawn, so the claim has to travel even when no label is baked.
    ...(platformSettings.linkedin.aiProvenance
      ? { aiProvenance: platformSettings.linkedin.aiProvenance }
      : {}),
    ...(platformSettings.linkedin.bakeAiLabel
      ? {
          bakeAiLabel: true,
          // Only sent alongside the label itself — the appearance of a
          // label that isn't being drawn is not a setting worth sending.
          ...(platformSettings.linkedin.aiLabelStyle
            ? { aiLabelStyle: platformSettings.linkedin.aiLabelStyle }
            : {}),
        }
      : {}),
  }

  const facebookBuilt = buildPostContentFromWorkspace(workspaceLike, 'facebook', {
    isStory: platformSettings.facebook.postAsStory,
    isReel: platformSettings.facebook.postAsReel,
  })
  const facebookResult =
    facebookBuilt.ok && facebookBuilt.content.platform === 'facebook'
      ? facebookBuilt.content.postContent
      : {
          type: 'invalid' as const,
          reason: facebookBuilt.ok ? 'platform mismatch' : facebookBuilt.reason,
        }
  const facebookContent: FacebookPostContent = (() => {
    const base: FacebookPostContent =
      facebookResult.type !== 'invalid'
        ? facebookResult
        : ({ type: 'text' as const, caption: captions.facebook || '' } as FacebookPostContent)
    let next: FacebookPostContent =
      base.type === 'reel' && videoThumbnailUrl ? { ...base, thumbUrl: videoThumbnailUrl } : base
    if (platformSettings.facebook.placeId) {
      next = { ...next, placeId: platformSettings.facebook.placeId }
    }
    // Targeting/feed-targeting JSON — accept-and-skip on parse failure rather
    // than blocking the post. Meta validates server-side anyway.
    const parseTargeting = (raw: string): Record<string, unknown> | undefined => {
      const trimmed = raw.trim()
      if (!trimmed) return undefined
      try {
        const parsed = JSON.parse(trimmed)
        return typeof parsed === 'object' && parsed !== null
          ? (parsed as Record<string, unknown>)
          : undefined
      } catch {
        return undefined
      }
    }
    const feedTargeting = parseTargeting(platformSettings.facebook.feedTargetingJson)
    const targeting = parseTargeting(platformSettings.facebook.targetingJson)
    if (feedTargeting) next = { ...next, feedTargeting }
    if (targeting) next = { ...next, targeting }
    if (next.type === 'video') {
      if (platformSettings.facebook.videoTitle) {
        next = { ...next, title: platformSettings.facebook.videoTitle }
      }
      if (platformSettings.facebook.contentCategory) {
        next = { ...next, contentCategory: platformSettings.facebook.contentCategory }
      }
    }
    return next
  })()
  const facebookSettings: FacebookSettings = {
    // Per-platform draft toggle. Previously this was wired to the global
    // publishAsDraft flag — but that flag is LinkedIn-specific in the UI, so
    // toggling it would silently affect Facebook posts in the same publish
    // batch. Now Facebook owns its own draft state via platformSettings.
    published: !platformSettings.facebook.postAsDraft,
    ...(platformSettings.facebook.aiDisclosure
      ? { aiDisclosure: platformSettings.facebook.aiDisclosure }
      : {}),
    // Sent independently of the label: an Instagram crop or a TikTok
    // transcode strips the IPTC marker whether or not anything visible is
    // drawn, so the claim has to travel even when no label is baked.
    ...(platformSettings.facebook.aiProvenance
      ? { aiProvenance: platformSettings.facebook.aiProvenance }
      : {}),
    ...(platformSettings.facebook.bakeAiLabel
      ? {
          bakeAiLabel: true,
          // Only sent alongside the label itself — the appearance of a
          // label that isn't being drawn is not a setting worth sending.
          ...(platformSettings.facebook.aiLabelStyle
            ? { aiLabelStyle: platformSettings.facebook.aiLabelStyle }
            : {}),
        }
      : {}),
  }

  // Per-platform built post content, indexed by platform. Adding a platform = adding one entry.
  type PreviewPostContent =
    | LinkedInPostContent
    | InstagramPostContent
    | TikTokPostContent
    | YouTubePostContent
    | FacebookPostContent
  const postContentByPlatform: Record<PublishablePlatformType, PreviewPostContent> = {
    linkedin: linkedInContent,
    instagram: instagramContent,
    tiktok: tiktokContent,
    youtube: youtubeContent,
    facebook: facebookContent,
  }

  // validation
  const captionLengths: CaptionLengths = {
    instagram: captions.instagram?.length || 0,
    linkedin: captions.linkedin?.length || 0,
    tiktok: captions.tiktok?.length || 0,
    youtube: captions.youtube?.length || 0,
    facebook: captions.facebook?.length || 0,
    'meta-ads': captions['meta-ads']?.length || 0,
  }
  const hasCaption = Object.values(captionLengths).some((n) => n > 0)
  const postType = analyzePostType(workspaceMedia, hasCaption)
  const contentCompatibility = checkCompatibility(postType, captionLengths)
  const validation = validateWorkspacePreview({
    contentCompatibility,
    selectedPlatform,
    selectedAccounts,
    accounts,
    isAuthenticated,
    activePlatforms,
  })
  const { compatibility, warnings, showPreview } = validation

  // platform count
  const selectedPlatforms = Array.from(
    new Set(
      selectedAccounts
        .filter((acc) => activePlatforms.includes(acc.platform))
        .map((acc) => acc.platform),
    ),
  )
  const compatibleSelectedPlatforms = selectedPlatforms.filter(
    (platform) => compatibility[platform].compatible,
  )
  const platformCount = compatibleSelectedPlatforms.length

  // Instagram, TikTok and YouTube cannot post without media. When the workspace
  // has no usable media (image still generating, upload incomplete, or none
  // attached), their postContent builder returns `invalid` and the bases above
  // fall back to a placeholder with an empty `url`. Publishing/scheduling that
  // placeholder creates a doomed post (and previously even reported success).
  // Block submission while any selected media-required platform isn't ready.
  const mediaRequiredResults = {
    instagram: instagramResult,
    tiktok: tiktokResult,
    youtube: youtubeResult,
  } as const
  const notReadyPlatforms = selectedPlatforms.filter(
    (platform): platform is keyof typeof mediaRequiredResults =>
      platform in mediaRequiredResults &&
      mediaRequiredResults[platform as keyof typeof mediaRequiredResults].type === 'invalid',
  )
  const contentNotReady = notReadyPlatforms.length > 0
  // Surface the builder's actual rejection reason per platform. Each invalid
  // result carries the specific cause (missing media, caption too long, too
  // many hashtags, unsupported format, …); collapsing them all into a blanket
  // "needs an image or video" misleads users who DID add media but tripped a
  // different rule.
  const contentNotReadyMessage = contentNotReady
    ? `⚠️ ${notReadyPlatforms
        .map((p) => {
          const result = mediaRequiredResults[p]
          const name = p.charAt(0).toUpperCase() + p.slice(1)
          return result.type === 'invalid' ? `${name}: ${result.reason}` : name
        })
        .join(' · ')}`
    : null

  // handlers
  const convertAndUploadCarouselPdf = async (
    imageSources: (string | Blob | File)[],
    uploadConfig:
      | { type: 'workspace'; workspaceId: number }
      | { type: 'scheduledPost'; scheduledPostId: number },
    caption: string,
  ): Promise<{ url: string; caption: string }> => {
    const pdfFile = await convertImagesToPdf(imageSources, 'linkedin-carousel.pdf')

    const uploadResult = await getUploadUrl(uploadConfig)
    await fetch(uploadResult.uploadUrl, { method: 'PUT', body: pdfFile })

    // For workspace, use assetUrl if available; for scheduledPost, use uploadUrl without query params
    const url: string =
      uploadConfig.type === 'workspace' && uploadResult.assetUrl
        ? uploadResult.assetUrl
        : uploadResult.uploadUrl.split('?')[0]!

    return { url, caption }
  }

  const getMediaFiles = async () => {
    const files: { url: string; blob: Blob; file: File }[] = []

    for (const media of workspaceMedia) {
      if (media.file.url) {
        try {
          const response = await fetch(media.file.url)
          const blob = await response.blob()
          const file = new File(
            [blob],
            `media-${files.length}.${media.type === 'video' ? 'mp4' : media.type === 'document' ? 'pdf' : 'png'}`,
            {
              type:
                media.type === 'video'
                  ? 'video/mp4'
                  : media.type === 'document'
                    ? 'application/pdf'
                    : 'image/png',
            },
          )
          files.push({ url: media.file.url, blob, file })
        } catch (error) {
          console.error('Error fetching media:', error)
        }
      }
    }

    return files
  }

  const handleLinkedInPost = async (account: SelectedAccount) => {
    let postContent: LinkedInPostContent = linkedInContent

    // Only fetch media in the browser for carousel PDF conversion.
    // For all other types (image, video, document, text), the backend
    // fetches the asset server-side from the URL — no browser fetch needed.
    if (postAsCarousel && linkedInContent.type === 'multiImage' && linkedInContent.urls) {
      try {
        if (!workspaceId) {
          throw new Error('Workspace ID is required to upload PDF')
        }

        const mediaFiles = await getMediaFiles()
        const imageFiles = mediaFiles.filter((f) => f.file.type.startsWith('image/'))

        // Map URLs to blobs if available, otherwise use URLs
        const imageSources: (string | Blob | File)[] = linkedInContent.urls.map((entry) => {
          const url = multiImageEntryUrl(entry)
          const mediaFile = imageFiles.find((f) => f.url === url)
          return mediaFile ? mediaFile.blob : url
        })

        const { url, caption } = await convertAndUploadCarouselPdf(
          imageSources,
          {
            type: 'workspace',
            workspaceId: workspaceId,
          },
          linkedInContent.caption || '',
        )
        // Create document post (LinkedIn converts to carousel)
        postContent = {
          type: 'document',
          url,
          caption,
          title: workspaceTitle,
        }
      } catch (error) {
        captureError(error, {
          flow: 'workspace-preview.carouselPdf',
          workspaceId,
          imageCount: linkedInContent.urls?.length ?? 0,
        })
        toast.error('Failed to convert images to PDF for carousel. Post cancelled.')
        throw error
      }
    }

    const result = await createPost({
      platform: 'linkedin',
      accountId: account.accountId,
      workspaceId,
      postContent,
      settings: linkedinSettings,
    })
    return result
  }

  const handleInstagramPost = async (account: SelectedAccount) => {
    const result = await createPost({
      platform: 'instagram',
      accountId: account.accountId,
      workspaceId,
      postContent: instagramContent,
      settings: instagramSettings,
    })
    return result
  }

  const handleTikTokPost = async (account: SelectedAccount) => {
    // TikTok uses PULL_FROM_URL for both flows — backend fetches the media
    // server-side, so the existing S3 URL(s) on `tiktokContent` are already
    // what we need. Video posts also have their URL re-asserted from the
    // workspace media item for parity with the original code path; photo
    // posts pass through whatever `photoImages` were assembled upstream.
    let postContent: TikTokPostContent
    if (tiktokContent.type === 'video') {
      const videoMedia = workspaceMedia.find((m) => m.type === 'video')
      if (!videoMedia?.file.url) {
        throw new Error('TikTok requires a video file')
      }
      postContent = { ...tiktokContent, url: videoMedia.file.url }
    } else {
      // Photo carousel — already assembled by tiktokPostContent() upstream.
      if (tiktokContent.photoImages.length === 0) {
        throw new Error('TikTok photo posts require at least one image')
      }
      postContent = tiktokContent
    }

    const result = await createPost({
      platform: 'tiktok',
      accountId: account.accountId,
      workspaceId,
      postContent,
      settings: tiktokSettings,
    })
    return result
  }

  const handleYouTubePost = async (account: SelectedAccount) => {
    // Find the video directly from workspace media (skip browser fetch to avoid CORS)
    const videoMedia = workspaceMedia.find((m) => m.type === 'video')

    if (!videoMedia?.file.url) {
      throw new Error('YouTube requires a video file')
    }

    // YouTube backend downloads the video from URL and uploads to YouTube API
    const postContent: YouTubePostContent = {
      ...youtubeContent,
      url: videoMedia.file.url,
    }

    const result = await createPost({
      platform: 'youtube',
      accountId: account.accountId,
      workspaceId,
      postContent,
      settings: youtubeSettings,
    })
    return result
  }

  const handleFacebookPost = async (account: SelectedAccount) => {
    const result = await createPost({
      platform: 'facebook',
      accountId: account.accountId,
      workspaceId,
      postContent: facebookContent,
      settings: facebookSettings,
    })
    return result
  }

  const executePostAll = async () => {
    setIsPosting(true)
    setPlatformStatus(idlePlatformStatus())

    const results: { platform: string; account: string; success: boolean; error?: string }[] = []

    try {
      const postToAccount = async (
        account: SelectedAccount,
      ): Promise<{ platform: string; account: string; success: boolean; error?: string }> => {
        try {
          const platformCompatibility = compatibility[account.platform]

          if (!platformCompatibility.compatible) {
            setPlatformStatus((prev) => ({
              ...prev,
              [account.platform]: 'error',
            }))
            return {
              platform: account.platform,
              account: account.displayName,
              success: false,
              error: platformCompatibility.reason || 'Validation failed',
            }
          }

          setPlatformStatus((prev) => ({
            ...prev,
            [account.platform]: 'loading',
          }))

          if (account.platform === 'linkedin') {
            const result = await handleLinkedInPost(account)
            setPlatformStatus((prev) => ({
              ...prev,
              linkedin: 'success',
            }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { link: result.link, isScheduled: false },
            }))
            return { platform: 'LinkedIn', account: account.displayName, success: true }
          } else if (account.platform === 'instagram') {
            const result = await handleInstagramPost(account)
            setPlatformStatus((prev) => ({
              ...prev,
              instagram: 'success',
            }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { link: result.link, isScheduled: false },
            }))
            return { platform: 'Instagram', account: account.displayName, success: true }
          } else if (account.platform === 'tiktok') {
            const result = await handleTikTokPost(account)
            setPlatformStatus((prev) => ({
              ...prev,
              tiktok: 'success',
            }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { link: result.link, isScheduled: false },
            }))
            toast.info(
              tiktokContent.type === 'photo'
                ? 'Your photo post has been submitted to TikTok and is being processed. It may take a few moments to appear on your profile.'
                : 'Your video has been submitted to TikTok and is being processed. It may take a few moments to appear on your profile.',
              { duration: 8000 },
            )

            // 5e: Poll post status in the background
            if (result.platformPostId) {
              pollTikTokPostStatus(result.platformPostId)
            }

            return { platform: 'TikTok', account: account.displayName, success: true }
          } else if (account.platform === 'youtube') {
            const result = await handleYouTubePost(account)
            setPlatformStatus((prev) => ({
              ...prev,
              youtube: 'success',
            }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { link: result.link, isScheduled: false },
            }))
            return { platform: 'YouTube', account: account.displayName, success: true }
          } else if (account.platform === 'facebook') {
            const result = await handleFacebookPost(account)
            setPlatformStatus((prev) => ({
              ...prev,
              facebook: 'success',
            }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { link: result.link, isScheduled: false },
            }))
            return { platform: 'Facebook', account: account.displayName, success: true }
          } else {
            setPlatformStatus((prev) => ({
              ...prev,
              [account.platform]: 'error',
            }))
            return {
              platform: account.platform,
              account: account.displayName,
              success: false,
              error: 'Platform not yet supported',
            }
          }
        } catch (error) {
          captureError(error, {
            flow: 'workspace-preview.postToAccount',
            platform: account.platform,
            accountId: account.accountId,
            workspaceId,
            mediaCount: workspaceMedia.length,
          })
          const errorMessage = error instanceof Error ? error.message : String(error)
          setPlatformStatus((prev) => ({
            ...prev,
            [account.platform]: 'error',
          }))
          return {
            platform: account.platform,
            account: account.displayName,
            success: false,
            error: errorMessage,
          }
        }
      }

      const accountsToPost = selectedAccounts.filter((acc) =>
        activePlatforms.includes(acc.platform),
      )

      if (accountsToPost.length === 0) {
        toast.error('Please select at least one account')
        setIsPosting(false)
        return
      }

      const postPromises = accountsToPost.map((account) => postToAccount(account))
      const settledResults = await Promise.allSettled(postPromises)

      settledResults.forEach((settledResult) => {
        if (settledResult.status === 'fulfilled') {
          results.push(settledResult.value)
        } else {
          results.push({
            platform: 'unknown',
            account: 'unknown',
            success: false,
            error: settledResult.reason?.toString() || 'Unexpected error',
          })
        }
      })

      const successCount = results.filter((r) => r.success).length
      const failCount = results.filter((r) => !r.success).length

      const hasLinkedInDraft = results.some(
        (r) => r.success && r.platform === 'LinkedIn' && publishAsDraft,
      )
      const successMessage = hasLinkedInDraft
        ? `Successfully ${publishAsDraft ? 'posted as draft' : 'published'} to ${successCount} account(s)! 🎉`
        : `Successfully published to ${successCount} account(s)! 🎉`

      // Browser-side twin of the backend's post_published: PostHog surveys can
      // only trigger on events the browser captures (the AppSumo review ask
      // keys off this one).
      if (successCount > 0) {
        posthog.capture('post_publish_succeeded', { mode: 'publish', count: successCount })
      }
      if (successCount > 0 && failCount === 0) {
        toast.success(successMessage)
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Published to ${successCount} account(s), ${failCount} failed`)
      } else {
        toast.error('Failed to publish to all accounts')
      }

      const failures = results.filter((r) => !r.success)
      failures.forEach((r) => {
        toast.error(`${r.platform} (${r.account}): ${r.error}`)
      })

      // Refresh isAuthenticated for any failed platform so the sync-status
      // banner (and the /account warning icon) reflect a needs_reauth state
      // the backend may have just written in its catch path.
      const failedPlatforms = new Set(failures.map((r) => r.platform as PublishablePlatformType))
      failedPlatforms.forEach((platform) => {
        trpcUtils.platform.isAuthenticated.invalidate({ platform })
      })

      if (successCount > 0) {
        setIsPostingComplete(true)
        if (workspaceId != null) trpcUtils.workspace.postStatus.invalidate({ id: workspaceId })
      }
    } finally {
      setIsPosting(false)
    }
  }

  const handlePostAll = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select at least one account')
      return
    }

    // Validate TikTok requirements before posting
    const hasTiktokAccount = selectedAccounts.some(
      (acc) => acc.platform === 'tiktok' && activePlatforms.includes('tiktok'),
    )
    if (hasTiktokAccount) {
      // 1b: Check if creator can post (empty privacy_level_options means restricted)
      if (tiktokCreatorInfo && tiktokCreatorInfo.privacy_level_options.length === 0) {
        toast.error('Your TikTok account is currently unable to post. Please try again later.')
        return
      }

      // 1c: Check video duration against max_video_post_duration_sec
      if (tiktokCreatorInfo) {
        const videoMedia = workspaceMedia.find((m) => m.type === 'video')
        if (videoMedia?.file.url) {
          try {
            const duration = await getVideoDuration(videoMedia.file.url)
            if (duration > tiktokCreatorInfo.max_video_post_duration_sec) {
              toast.error(
                `Video duration (${Math.round(duration)}s) exceeds TikTok's maximum of ${tiktokCreatorInfo.max_video_post_duration_sec}s for your account.`,
              )
              return
            }
          } catch {
            // If we can't determine duration, let TikTok API handle validation
            console.warn('[tiktok] Could not determine video duration, skipping client-side check')
          }
        }
      }

      // Open settings modal for TikTok confirmation before posting
      // The confirm button is disabled until privacy level and commercial content are valid
      if (onOpenSettingsForConfirm) {
        onOpenSettingsForConfirm(() => executePostAll(), 'Confirm & Publish')
        return
      }
    }

    executePostAll()
  }

  const executeScheduleAll = async (scheduledAt: Date) => {
    setIsScheduling(true)
    setPlatformStatus(idlePlatformStatus())

    const results: { platform: string; account: string; success: boolean; error?: string }[] = []

    try {
      const scheduleAccount = async (
        account: SelectedAccount,
      ): Promise<{ platform: string; account: string; success: boolean; error?: string }> => {
        // Tracks the scheduled_post row created below so we can roll it back if
        // a later step (copyAsset / updateScheduledPost) throws — otherwise the
        // row + its EventBridge schedule are orphaned, pointing at a workspace
        // URL that 404s once the workspace is deleted.
        let createdScheduledPostId: number | undefined
        const createScheduledPost = async (args: Parameters<typeof schedulePost>[0]) => {
          const data = await schedulePost(args)
          createdScheduledPostId = data.id
          return data
        }

        try {
          setPlatformStatus((prev) => ({
            ...prev,
            [account.platform]: 'loading',
          }))

          if (account.platform === 'linkedin') {
            const scheduledPostData = await createScheduledPost({
              platform: 'linkedin',
              accountId: account.accountId,
              workspaceId,
              postContent: linkedInContent,
              settings: linkedinSettings,
              scheduledAt,
            })

            let updatedContent: LinkedInPostContent = linkedInContent

            // Carousel: fetch images in browser (no CORS issues for images) and convert to PDF
            if (
              postAsCarousel &&
              linkedInContent.type === 'multiImage' &&
              linkedInContent.urls &&
              linkedInContent.urls.length > 1
            ) {
              try {
                const mediaFiles = await getMediaFiles()
                const imageFiles = mediaFiles.filter((f) => f.file.type.startsWith('image/'))
                const imageSources: (string | Blob | File)[] = linkedInContent.urls.map((entry) => {
                  const url = multiImageEntryUrl(entry)
                  const mediaFile = imageFiles.find((f) => f.url === url)
                  return mediaFile ? mediaFile.blob : url
                })

                const { url, caption } = await convertAndUploadCarouselPdf(
                  imageSources,
                  { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
                  linkedInContent.caption || '',
                )
                updatedContent = {
                  type: 'document',
                  url,
                  caption,
                  title: workspaceTitle,
                }
              } catch (error) {
                console.error('Error converting images to PDF:', error)
                toast.error('Failed to convert images to PDF for carousel. Post cancelled.')
                throw error
              }
            } else if (linkedInContent.type === 'image' || linkedInContent.type === 'video') {
              // Server-side S3 copy for single image/video
              const { url } = await copyAsset({
                sourceUrl: linkedInContent.url,
                destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
              })
              updatedContent = { ...linkedInContent, url }

              // Copy video thumbnail so the URL survives workspace deletion
              if (linkedInContent.type === 'video' && linkedInContent.thumbUrl) {
                const { url: thumbUrl } = await copyAsset({
                  sourceUrl: linkedInContent.thumbUrl,
                  destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
                })
                updatedContent = { ...updatedContent, thumbUrl } as LinkedInPostContent
              }
            }
            // multiImage (non-carousel), text, document, reshare — keep workspace URLs as-is

            // Reference equality: updatedContent only differs when carousel or image/video
            // paths above created a new object. If neither ran, skip the update call.
            if (updatedContent !== linkedInContent) {
              await updateScheduledPost({
                id: scheduledPostData.id,
                platform: 'linkedin',
                postContent: updatedContent,
                settings: linkedinSettings,
              })
            }

            setPlatformStatus((prev) => ({ ...prev, linkedin: 'success' }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { scheduledPostId: scheduledPostData.id, isScheduled: true },
            }))
            return { platform: 'LinkedIn', account: account.displayName, success: true }
          } else if (account.platform === 'instagram') {
            const scheduledPostData = await createScheduledPost({
              platform: 'instagram',
              accountId: account.accountId,
              workspaceId,
              postContent: instagramContent,
              settings: instagramSettings,
              scheduledAt,
            })

            // Copy media to scheduledPost path for non-carousel types
            if (
              instagramContent.type !== 'carousel' &&
              'url' in instagramContent &&
              instagramContent.url
            ) {
              const { url } = await copyAsset({
                sourceUrl: instagramContent.url,
                destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
              })

              let updated: InstagramPostContent = {
                ...instagramContent,
                url,
              } as InstagramPostContent

              // Copy reel thumbnail so the URL survives workspace deletion
              if (instagramContent.type === 'reel' && instagramContent.thumbUrl) {
                const { url: thumbUrl } = await copyAsset({
                  sourceUrl: instagramContent.thumbUrl,
                  destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
                })
                updated = { ...updated, thumbUrl } as InstagramPostContent
              }

              await updateScheduledPost({
                id: scheduledPostData.id,
                platform: 'instagram',
                postContent: updated,
                settings: instagramSettings,
              })
            }

            setPlatformStatus((prev) => ({ ...prev, instagram: 'success' }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { scheduledPostId: scheduledPostData.id, isScheduled: true },
            }))
            return { platform: 'Instagram', account: account.displayName, success: true }
          } else if (account.platform === 'tiktok') {
            const scheduledPostData = await createScheduledPost({
              platform: 'tiktok',
              accountId: account.accountId,
              workspaceId,
              postContent: tiktokContent,
              settings: tiktokSettings,
              scheduledAt,
            })

            if (tiktokContent.type === 'video' && tiktokContent.url) {
              const { url } = await copyAsset({
                sourceUrl: tiktokContent.url,
                destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
              })
              await updateScheduledPost({
                id: scheduledPostData.id,
                platform: 'tiktok',
                postContent: { ...tiktokContent, url },
                settings: tiktokSettings,
              })
            }

            setPlatformStatus((prev) => ({ ...prev, tiktok: 'success' }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { scheduledPostId: scheduledPostData.id, isScheduled: true },
            }))
            return { platform: 'TikTok', account: account.displayName, success: true }
          } else if (account.platform === 'youtube') {
            const scheduledPostData = await createScheduledPost({
              platform: 'youtube',
              accountId: account.accountId,
              workspaceId,
              postContent: youtubeContent,
              settings: youtubeSettings,
              scheduledAt,
            })

            if (youtubeContent.url) {
              const { url } = await copyAsset({
                sourceUrl: youtubeContent.url,
                destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
              })

              let updated = { ...youtubeContent, url }

              // Copy thumbnail so the URL survives workspace deletion
              if (youtubeContent.thumbUrl) {
                const { url: thumbUrl } = await copyAsset({
                  sourceUrl: youtubeContent.thumbUrl,
                  destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
                })
                updated = { ...updated, thumbUrl }
              }

              await updateScheduledPost({
                id: scheduledPostData.id,
                platform: 'youtube',
                postContent: updated,
                settings: youtubeSettings,
              })
            }

            setPlatformStatus((prev) => ({ ...prev, youtube: 'success' }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { scheduledPostId: scheduledPostData.id, isScheduled: true },
            }))
            return { platform: 'YouTube', account: account.displayName, success: true }
          } else if (account.platform === 'facebook') {
            const scheduledPostData = await createScheduledPost({
              platform: 'facebook',
              accountId: account.accountId,
              workspaceId,
              postContent: facebookContent,
              settings: facebookSettings,
              scheduledAt,
            })

            // Copy media to scheduledPost path for non-text post types so the
            // worker has a stable URL when the schedule fires. Reels with a
            // custom cover have both a video URL and a thumbnail URL — copy
            // both so neither survives a workspace delete pointing at a 404.
            if (
              facebookContent.type !== 'text' &&
              facebookContent.type !== 'link' &&
              facebookContent.type !== 'multiPhoto' &&
              'url' in facebookContent &&
              facebookContent.url
            ) {
              const { url } = await copyAsset({
                sourceUrl: facebookContent.url,
                destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
              })

              let updated: FacebookPostContent = {
                ...facebookContent,
                url,
              } as FacebookPostContent

              if (facebookContent.type === 'reel' && facebookContent.thumbUrl) {
                const { url: thumbUrl } = await copyAsset({
                  sourceUrl: facebookContent.thumbUrl,
                  destination: { type: 'scheduledPost', scheduledPostId: scheduledPostData.id },
                })
                updated = { ...updated, thumbUrl } as FacebookPostContent
              }

              await updateScheduledPost({
                id: scheduledPostData.id,
                platform: 'facebook',
                postContent: updated,
                settings: facebookSettings,
              })
            }

            setPlatformStatus((prev) => ({ ...prev, facebook: 'success' }))
            setPostLinks((prev) => ({
              ...prev,
              [account.platform]: { scheduledPostId: scheduledPostData.id, isScheduled: true },
            }))
            return { platform: 'Facebook', account: account.displayName, success: true }
          } else {
            setPlatformStatus((prev) => ({
              ...prev,
              [account.platform]: 'error',
            }))
            return {
              platform: account.platform,
              account: account.displayName,
              success: false,
              error: 'Platform not yet supported',
            }
          }
        } catch (error) {
          // Roll back a partially-created scheduled post so a failed media copy
          // doesn't leave an orphan row + schedule behind.
          if (createdScheduledPostId !== undefined) {
            try {
              await deleteScheduledPost({ id: createdScheduledPostId })
            } catch (rollbackError) {
              captureError(rollbackError, {
                flow: 'workspace-preview.scheduleToAccount.rollback',
                platform: account.platform,
                scheduledPostId: createdScheduledPostId,
              })
            }
          }
          captureError(error, {
            flow: 'workspace-preview.scheduleToAccount',
            platform: account.platform,
            accountId: account.accountId,
            workspaceId,
            mediaCount: workspaceMedia.length,
          })
          const errorMessage = error instanceof Error ? error.message : String(error)
          setPlatformStatus((prev) => ({
            ...prev,
            [account.platform]: 'error',
          }))
          return {
            platform: account.platform,
            account: account.displayName,
            success: false,
            error: errorMessage,
          }
        }
      }

      // Schedule posts to all accounts from all active platforms
      const accountsToSchedule = selectedAccounts.filter((acc) =>
        activePlatforms.includes(acc.platform),
      )

      if (accountsToSchedule.length === 0) {
        toast.error('Please select at least one account')
        setIsScheduling(false)
        return
      }

      const schedulePromises = accountsToSchedule.map((account) => scheduleAccount(account))
      const settledResults = await Promise.allSettled(schedulePromises)

      settledResults.forEach((settledResult) => {
        if (settledResult.status === 'fulfilled') {
          results.push(settledResult.value)
        } else {
          results.push({
            platform: 'unknown',
            account: 'unknown',
            success: false,
            error: settledResult.reason?.toString() || 'Unexpected error',
          })
        }
      })

      const successCount = results.filter((r) => r.success).length
      const failCount = results.filter((r) => !r.success).length

      if (successCount > 0) {
        posthog.capture('post_publish_succeeded', { mode: 'schedule', count: successCount })
      }
      if (successCount > 0 && failCount === 0) {
        toast.success(`Successfully scheduled ${successCount} post(s)! 🎉`)
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Scheduled ${successCount} post(s), ${failCount} failed`)
      } else {
        toast.error('Failed to schedule all posts')
      }

      results
        .filter((r) => !r.success)
        .forEach((r) => {
          toast.error(`${r.platform} (${r.account}): ${r.error}`)
        })

      if (successCount > 0) {
        setIsSchedulingComplete(true)
        if (workspaceId != null) trpcUtils.workspace.postStatus.invalidate({ id: workspaceId })
      }
    } finally {
      setIsScheduling(false)
    }
  }

  const handleScheduleAll = async (scheduledAt: Date) => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select at least one account')
      return
    }

    // Open settings modal for TikTok confirmation before scheduling
    const hasTiktokAccount = selectedAccounts.some(
      (acc) => acc.platform === 'tiktok' && activePlatforms.includes('tiktok'),
    )
    if (hasTiktokAccount) {
      // Open settings modal for TikTok confirmation before scheduling
      // The confirm button is disabled until privacy level and commercial content are valid
      if (onOpenSettingsForConfirm) {
        onOpenSettingsForConfirm(() => executeScheduleAll(scheduledAt), 'Confirm & Schedule')
        return
      }
    }

    executeScheduleAll(scheduledAt)
  }

  /**
   * Re-bakes a pending scheduled post's content from the current workspace
   * state (the drift "Update content" action in the status strip). Content is
   * assembled exactly like an immediate publish — workspace-hosted URLs, no
   * asset copy: since workspaces with scheduled posts can't be deleted and
   * referenced objects survive asset.delete, those URLs stay resolvable.
   */
  const updateScheduledContent = async (entry: { id: number; platform: string }) => {
    const platform = entry.platform as PublishablePlatformType
    // Content-only check, NOT the combined `compatibility`: that one also
    // fails when no account is selected in the modal — irrelevant here, the
    // scheduled post already has its account (and this runs from the header
    // with the modal closed, where nothing is selected).
    if (!contentCompatibility[platform]?.compatible) {
      toast.error(
        contentCompatibility[platform]?.reason ?? 'Content is not valid for this platform',
      )
      return
    }

    try {
      if (platform === 'linkedin') {
        let postContent: LinkedInPostContent = linkedInContent
        if (
          postAsCarousel &&
          linkedInContent.type === 'multiImage' &&
          linkedInContent.urls &&
          workspaceId
        ) {
          const mediaFiles = await getMediaFiles()
          const imageFiles = mediaFiles.filter((f) => f.file.type.startsWith('image/'))
          const imageSources: (string | Blob | File)[] = linkedInContent.urls.map((entry) => {
            const url = multiImageEntryUrl(entry)
            const mediaFile = imageFiles.find((f) => f.url === url)
            return mediaFile ? mediaFile.blob : url
          })
          const { url, caption } = await convertAndUploadCarouselPdf(
            imageSources,
            { type: 'workspace', workspaceId },
            linkedInContent.caption || '',
          )
          postContent = { type: 'document', url, caption, title: workspaceTitle }
        }
        await updateScheduledPost({
          id: entry.id,
          platform: 'linkedin',
          postContent,
          settings: linkedinSettings,
        })
      } else if (platform === 'instagram') {
        await updateScheduledPost({
          id: entry.id,
          platform: 'instagram',
          postContent: instagramContent,
          settings: instagramSettings,
        })
      } else if (platform === 'tiktok') {
        const videoMedia = workspaceMedia.find((m) => m.type === 'video')
        const postContent: TikTokPostContent =
          tiktokContent.type === 'video' && videoMedia?.file.url
            ? { ...tiktokContent, url: videoMedia.file.url }
            : tiktokContent
        await updateScheduledPost({
          id: entry.id,
          platform: 'tiktok',
          postContent,
          settings: tiktokSettings,
        })
      } else if (platform === 'youtube') {
        const videoMedia = workspaceMedia.find((m) => m.type === 'video')
        if (!videoMedia?.file.url) {
          toast.error('YouTube requires a video file')
          return
        }
        await updateScheduledPost({
          id: entry.id,
          platform: 'youtube',
          postContent: { ...youtubeContent, url: videoMedia.file.url },
          settings: youtubeSettings,
        })
      } else if (platform === 'facebook') {
        await updateScheduledPost({
          id: entry.id,
          platform: 'facebook',
          postContent: facebookContent,
          settings: facebookSettings,
        })
      }

      if (workspaceId != null) trpcUtils.workspace.postStatus.invalidate({ id: workspaceId })
      toast.success('Scheduled post updated with the current workspace content')
    } catch (error) {
      toast.error(
        error instanceof TRPCClientError ? error.message : 'Failed to update the scheduled post',
      )
    }
  }

  // Hand the re-sync handler to the workspace header (plain ref assignment on
  // render, same pattern as ratingAnalyzeRef on the workspace page).
  if (updateScheduledContentRef) updateScheduledContentRef.current = updateScheduledContent

  // effects

  useEffect(() => {
    const getSelectedAccount = (platform: PublishablePlatformType) => {
      return selectedAccounts.find((acc) => acc.platform === platform)
    }

    const handleSelectAccount = (platform: PublishablePlatformType, account: Account) => {
      const filteredAccounts = selectedAccounts.filter((acc) => acc.platform !== platform)

      const newAccount: SelectedAccount = {
        platform,
        accountId: account.id,
        displayName: account.displayName,
        imageUrl: account.imageUrl,
      }

      setSelectedAccounts([...filteredAccounts, newAccount])
    }

    if (!open) return

    if (
      selectedPlatform === 'instagram' &&
      isAuthenticated.instagram &&
      accounts.instagram.length > 0 &&
      !getSelectedAccount('instagram')
    ) {
      handleSelectAccount('instagram', accounts.instagram[0]!)
    }
  }, [
    open,
    selectedPlatform,
    isAuthenticated.instagram,
    accounts.instagram,
    selectedAccounts,
    setSelectedAccounts,
  ])

  useEffect(() => {
    if (!open) {
      setPlatformStatus(idlePlatformStatus())
      setIsPostingComplete(false)
      setIsSchedulingComplete(false)
      setPostLinks({})
      setTiktokProcessing(false)
    }
  }, [open])

  // Sync selectedPlatform with activePlatforms when modal opens or activePlatforms changes
  useEffect(() => {
    if (open && activePlatforms.length > 0) {
      // If current selected platform is not in active platforms, switch to the
      // first *publishable* active platform (Meta Ads et al. can't be published).
      if (!activePlatforms.includes(selectedPlatform)) {
        const firstPublishable = activePlatforms.find(isPublishablePlatform)
        if (firstPublishable) setSelectedPlatform(firstPublishable)
      }
    }
  }, [open, activePlatforms, selectedPlatform])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex h-full w-full max-w-[700px] flex-col',
          isPosting || isScheduling || isPostingComplete || isSchedulingComplete
            ? 'max-h-fit'
            : 'max-h-[95vh]',
          theme === 'dark' ? 'dark' : '',
        )}>
        <DialogHeader className="items-center">
          <DialogTitle className="flex items-center gap-2">
            {isPostingComplete || isSchedulingComplete ? null : (
              <StarsIcon width={22} height={22} color="var(--foreground)" />
            )}
            {isPostingComplete || isSchedulingComplete
              ? '✨ Well done!'
              : isPosting || isScheduling
                ? isScheduling
                  ? 'Scheduling your content...'
                  : publishAsDraft &&
                      selectedAccounts.some((acc) => acc.platform === 'linkedin') &&
                      !selectedAccounts.some((acc) => acc.platform !== 'linkedin')
                    ? 'Posting your draft...'
                    : 'Publishing your content...'
                : 'Preview Post'}
          </DialogTitle>
          <DialogDescription>
            {isPostingComplete || isSchedulingComplete
              ? publishAsDraft && selectedAccounts.some((acc) => acc.platform === 'linkedin')
                ? selectedAccounts.some((acc) => acc.platform !== 'linkedin')
                  ? 'Your posts have been published and your LinkedIn draft has been posted below'
                  : 'Your LinkedIn draft has been posted below'
                : 'Go view your posts below'
              : isPosting || isScheduling
                ? isScheduling
                  ? 'We are scheduling your post across all selected platforms'
                  : publishAsDraft && selectedAccounts.some((acc) => acc.platform === 'linkedin')
                    ? selectedAccounts.some((acc) => acc.platform !== 'linkedin')
                      ? 'We are publishing your post across selected platforms and posting your draft to LinkedIn'
                      : 'We are posting your draft to LinkedIn'
                    : 'We are publishing your post across all selected platforms'
                : 'Preview your post before publishing'}
          </DialogDescription>
        </DialogHeader>

        {/* Show posting screen when posting or scheduling, otherwise show preview */}
        {isPosting || isScheduling || isPostingComplete || isSchedulingComplete ? (
          <WorkspacePreviewStatus
            selectedAccounts={selectedAccounts.filter((acc) =>
              activePlatforms.includes(acc.platform),
            )}
            platformStatus={platformStatus}
            isScheduling={isScheduling || isSchedulingComplete}
            isComplete={isPostingComplete || isSchedulingComplete}
            postLinks={postLinks}
            publishAsDraft={publishAsDraft}
            hasVideo={workspaceMedia.some((m) => m.type === 'video')}
            tiktokProcessing={tiktokProcessing}
            onViewPost={(_platform, link, _scheduledPostId, isScheduled) => {
              if (isScheduled) {
                navigate('/posts')
              } else if (link) {
                window.open(link, '_blank')
              }
            }}
          />
        ) : (
          <>
            <WorkspacePreviewToggle
              selectedPlatform={selectedPlatform}
              onPlatformChange={(p) => {
                if (isPublishablePlatform(p)) setSelectedPlatform(p)
              }}
              compatibility={compatibility}
              activePlatforms={activePlatforms}
              onActivePlatformsChange={onActivePlatformsChange}
            />

            <WorkspacePreviewSyncStatus
              selectedPlatforms={[...new Set(selectedAccounts.map((a) => a.platform))]}
            />

            <WorkspacePreviewPostStatus
              workspaceId={workspaceId}
              open={open}
              platform={selectedPlatform}
            />

            {warnings.length > 0 && <WorkspacePreviewValidation warnings={warnings} />}

            <WorkspacePreviewAccountSelection
              selectedPlatform={selectedPlatform}
              selectedAccounts={selectedAccounts}
              onAccountsChange={setSelectedAccounts}
              accounts={accounts}
              isAuthenticated={isAuthenticated}
            />

            <WorkspacePreviewContent
              showPreview={showPreview ?? false}
              selectedPlatform={selectedPlatform}
              currentAccount={accountsByPlatform[selectedPlatform]?.[0]}
              currentPostContent={postContentByPlatform[selectedPlatform]}
              postAsCarousel={postAsCarousel}
              youtubePostAsShort={youtubePostAsShort}
              showAiLabel={
                showAiLabelInPreview && willBakeAiLabel(platformSettings, selectedPlatform)
              }
              aiLabelStyle={aiLabelStyle}
              carouselImageUrls={
                postAsCarousel && selectedPlatform === 'linkedin'
                  ? workspaceMedia
                      .filter((m) => (m.type === 'image' || m.type === 'canvas') && m.file.url)
                      .map((m) => m.file.url)
                  : undefined
              }
            />
          </>
        )}

        {/* Footer */}

        {!isPosting && !isScheduling && !isPostingComplete && !isSchedulingComplete && (
          <div
            className={cn(
              'border-border bg-background fixed bottom-0 left-1/2 flex w-full -translate-x-1/2 items-center justify-center rounded-b-lg border-t p-4',
            )}>
            {/* Wraps on phones: beside the buttons the message squeezed into a
                one-word-wide column. */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <p className="w-full text-center text-sm sm:w-auto">
                {contentNotReady
                  ? contentNotReadyMessage
                  : platformCount > 0
                    ? (() => {
                        const hasLinkedIn = selectedAccounts.some(
                          (acc) => acc.platform === 'linkedin',
                        )
                        const hasOtherPlatforms = selectedAccounts.some(
                          (acc) => acc.platform !== 'linkedin',
                        )
                        if (publishAsDraft && hasLinkedIn) {
                          if (hasOtherPlatforms) {
                            const otherPlatformCount = selectedAccounts.filter(
                              (acc) => acc.platform !== 'linkedin',
                            ).length
                            return `🚀 Your content will be published to ${otherPlatformCount} platform${otherPlatformCount > 1 ? 's' : ''} and posted as a draft on LinkedIn`
                          }
                          return `🚀 Your content will be posted as a draft on LinkedIn`
                        }
                        return `🚀 Your content will be published across ${platformCount} platform${platformCount > 1 ? 's' : ''}`
                      })()
                    : '⚠️ Check your content to publish or schedule'}
              </p>
              <PostPreviewScheduleButton
                onClick={handleScheduleAll}
                disabled={isScheduling || isPosting || platformCount === 0 || contentNotReady}
                newButton={true}
              />

              <PostButton
                onClick={handlePostAll}
                disabled={isPosting || isScheduling || platformCount === 0 || contentNotReady}
              />
              {onSettingsClick && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="hover:bg-hover rounded-lg border-none"
                        onClick={onSettingsClick}>
                        <SettingsIcon width={22} height={22} color="var(--foreground)" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Settings</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
